import { type AxialCoord, hexDistance, hexEquals, hexKey } from "../hex";
import { isWorkingCity, workingCities } from "../land/cities";
import { canTake, heldByCity } from "../land/territory";
import { bestBy, firstAccepted, nearestTo, rankBy } from "../pick";
import { findHex, isOwnGround, ownGround, unitAt, unitById, unitsOf } from "../run/board";
import { scriptUnitsOf } from "../run/seats";
import { allied, hostile } from "../run/sides";
import {
  type FactionId,
  type HexState,
  type RunState,
  UNIT_KINDS,
  type Unit,
  type UnitKind,
} from "../types/state";
import { isEngaged, underFire } from "../units/combat";
import { buildUnit, hasKind, unitCount } from "../units/roster";
import { orderUnit } from "../units/routes";
import { unitStats } from "../units/unit";
import { commandersOf } from "./commanders";
import { musteringCommander } from "./generalScript";
import { commandRefusal } from "./king";

/**
 * The court script's army: the kind a scripted kingdom trains (the counter-pick, also City
 * Command's Auto), recruiting in its idle yards, and where every idle unit goes (a commander with
 * room, a fight, land to take, a shelter for the wounded). `scriptedCommands` is the pair.
 */

/** What beats what: the script builds the counter to the kind it sees most. */
export const COUNTERS: Readonly<Record<UnitKind, UnitKind>> = {
  spear: "archer",
  sword: "spear",
  archer: "sword",
  rider: "spear",
  siege: "rider",
};

/**
 * The kind the script recruits next: the counter to the opposing kind it sees most within
 * `aiSightRange` of its land (ties by roster order); with nothing in sight it alternates
 * Sword and Spear by its own unit count, so it always has something that walks.
 */
export function counterPick(state: RunState, faction: FactionId): UnitKind {
  const own = ownGround(state, faction);
  // A51: a walled city of another faction in sight, and no Siege of its own, calls for one.
  const walledCityInSight = state.hexes.some(
    (hex) =>
      hostile(state.rules, hex.owner, faction) &&
      isWorkingCity(hex, state.tick) &&
      hex.walls > 0 &&
      own.some((land) => hexDistance(land.coord, hex.coord) <= state.rules.aiSightRange),
  );
  if (walledCityInSight && !hasKind(state, faction, "siege")) {
    return "siege";
  }
  const counts = new Map<UnitKind, number>();
  for (const unit of state.units) {
    if (!hostile(state.rules, unit.owner, faction)) {
      continue;
    }
    const seen = own.some((hex) => hexDistance(hex.coord, unit.coord) <= state.rules.aiSightRange);
    if (seen) {
      counts.set(unit.kind, (counts.get(unit.kind) ?? 0) + 1);
    }
  }
  const most = bestBy(
    UNIT_KINDS.filter((kind) => (counts.get(kind) ?? 0) > 0),
    (kind) => counts.get(kind) ?? 0,
  );
  if (most) {
    return COUNTERS[most];
  }
  return unitCount(state, faction) % 2 === 0 ? "sword" : "spear";
}

/** Every working city with an idle yard recruits the counter-pick while room and supply last. */
function scriptedRecruit(state: RunState, faction: FactionId): RunState {
  let next = state;
  for (const city of workingCities(state, faction)) {
    if (city.unitBuild !== "none") {
      continue;
    }
    next = buildUnit(next, faction, city.coord, counterPick(next, faction));
  }
  return next;
}

/**
 * Where the script's wounded unit falls back to (ADR-0013 slice 1): the nearest own, unrazed,
 * free hex no enemy can strike (insertion-order ties), and only with an own city or another own
 * unit within `aiSightRange`, so a unit alone in the field fights on. Undefined when none.
 */
function scriptedShelter(state: RunState, faction: FactionId, unit: Unit): AxialCoord | undefined {
  const sight = state.rules.aiSightRange;
  const company =
    state.units.some(
      (other) =>
        other.owner === faction &&
        other.id !== unit.id &&
        hexDistance(other.coord, unit.coord) <= sight,
    ) ||
    state.hexes.some(
      (hex) =>
        hex.owner === faction &&
        hex.structure === "city" &&
        hexDistance(hex.coord, unit.coord) <= sight,
    );
  if (!company) {
    return undefined;
  }
  return nearestTo(
    state.hexes,
    unit.coord,
    (hex) =>
      isOwnGround(hex, faction) &&
      !hexEquals(hex.coord, unit.coord) &&
      !unitAt(state, hex.coord) &&
      !underFire(state, faction, hex.coord),
  )?.coord;
}

/**
 * Every idle unit of the faction gets somewhere to go: the nearest commander with room, else
 * the nearest hex holding an enemy unit
 * within `aiSightRange`, else the nearest neutral hex (siege units prefer walled ones; one
 * another own unit is already walking to is left to it), and only when no neutral is left
 * the nearest hex of the other faction: it settles the land before it goes to war, so a new
 * player is not overrun in the first minute. Hex distance, then insertion order, and only
 * where a road exists.
 * A unit standing on a wall-free foreign hex stays until it has taken it; a unit whose
 * destination is still foreign or still holds an enemy keeps it.
 */
function scriptedOrders(state: RunState, faction: FactionId): RunState {
  let next = state;
  const claimed = new Set<string>();
  for (const unit of unitsOf(next, faction)) {
    if (unit.destination) {
      claimed.add(hexKey(unit.destination));
    }
  }
  const enemyAt = (coord: AxialCoord): boolean => {
    const standing = unitAt(next, coord);
    return standing !== undefined && hostile(next.rules, standing.owner, faction);
  };
  for (const unit of scriptUnitsOf(state, faction)) {
    const current = unitById(next, unit.id);
    if (!current || current.commander || current.squadOf !== undefined || current.governs) {
      continue;
    }
    // A unit the King cannot command now costs nothing to skip; ranking the map for it would.
    if (commandRefusal(next, faction, current.coord, "order") !== undefined) {
      continue;
    }
    if (canTake(next, faction, current.coord)) {
      continue;
    }
    // ADR-0013 slice 1: a unit under half its health breaks contact (the nearest own hex nobody
    // can strike, with an own city or unit in sight) and, once out of the fight, stands to heal.
    if (current.hp * 2 < unitStats(next.rules, current.kind).hp) {
      if (!isEngaged(next, current)) {
        continue;
      }
      const shelter = scriptedShelter(next, faction, current);
      if (shelter) {
        const sent = orderUnit(next, faction, current.id, shelter);
        if (sent !== next) {
          next = sent;
          claimed.add(hexKey(shelter));
          continue;
        }
      }
    }
    if (current.destination) {
      const goal = findHex(next, current.destination);
      const joining = commandersOf(next, faction).some((commander) =>
        hexEquals(commander.coord, current.destination ?? current.coord),
      );
      if (joining || (goal && (!allied(next.rules, goal.owner, faction) || enemyAt(goal.coord)))) {
        continue;
      }
      claimed.delete(hexKey(current.destination));
    }
    // A commander with room comes first: a soldier sent to its hex joins the squad on arrival.
    const leader = current.destination
      ? undefined
      : musteringCommander(next, faction, current.coord);
    if (leader) {
      const sent = orderUnit(next, faction, current.id, leader.coord);
      if (sent !== next) {
        next = sent;
        continue;
      }
    }
    const wantsWalls = unitStats(next.rules, current.kind).wallAttack > 1;
    const distance = (hex: HexState): number => hexDistance(hex.coord, current.coord);
    const priority = (hex: HexState): number => {
      const foe = enemyAt(hex.coord) && distance(hex) <= next.rules.aiSightRange;
      const walled = wantsWalls && hex.walls > 0;
      const spoken = hex.owner === "none" && claimed.has(hexKey(hex.coord));
      const opposing = hostile(next.rules, hex.owner, faction);
      return foe ? 0 : walled ? 1 : opposing ? 4 : spoken ? 3 : 2;
    };
    // Land a working city holds (A51) is no destination: only the city itself, or a unit on it, is.
    const goals = next.hexes.filter(
      (hex) =>
        !hex.razed &&
        (enemyAt(hex.coord) ||
          (!allied(next.rules, hex.owner, faction) && !heldByCity(next, hex.coord))),
    );
    const sent = firstAccepted(next, rankBy(goals, priority, distance), (from, hex) =>
      orderUnit(from, faction, current.id, hex.coord),
    );
    if (sent) {
      next = sent.next;
      claimed.add(hexKey(sent.chosen.coord));
    }
  }
  return next;
}

/** The script's commands without its King's walk: what a King who never leaves his hex can do. */
export function scriptedCommands(state: RunState, faction: FactionId): RunState {
  return scriptedOrders(scriptedRecruit(state, faction), faction);
}
